import React, { useEffect, useRef, useState } from "react";
import { FileOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

/** Fixed sidebar width when open — used for layout and fit-zoom. */
export const PAGE_THUMBNAIL_SIDEBAR_WIDTH = 128;
/** Narrow rail when collapsed — used for layout and fit-zoom. */
export const PAGE_THUMBNAIL_COLLAPSED_WIDTH = 20;

const THUMB_MAX_WIDTH = 96;
const SIDEBAR_TRANSITION =
  "width 0.28s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const CACHE_VERSION = "folder-pages-v1";
const MAX_PDF_DOCUMENTS = 24;
const MAX_PAGE_THUMBNAILS = 300;
const MAX_PAGE_COUNTS = 100;
const pageThumbCache = new Map();
const pageCountCache = new Map();
const pdfDocPromiseCache = new Map();
let cacheConsumerCount = 0;
let thumbnailCacheGeneration = 0;

const toggleButtonStyle = {
  position: "absolute",
  top: "50%",
  right: 0,
  transform: "translate(50%, -50%)",
  zIndex: 2,
  width: 18,
  height: 48,
  padding: 0,
  border: "1px solid #1a365d",
  borderRadius: 9,
  background: "#fff",
  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#1a365d",
  transition:
    "color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease",
};

const applyToggleHover = (el, hovering) => {
  if (!el) return;
  el.style.color = hovering ? "#0f2744" : "#1a365d";
  el.style.borderColor = hovering ? "#0f2744" : "#1a365d";
  el.style.boxShadow = hovering
    ? "0 2px 8px rgba(26, 54, 93, 0.2)"
    : "0 1px 4px rgba(0, 0, 0, 0.08)";
  el.style.background = hovering ? "#f7fafc" : "#fff";
};

const pageCacheKey = (fileUrl, pageNumber) =>
  `${CACHE_VERSION}:${fileUrl}:${pageNumber}`;

const getCachedValue = (cache, key) => {
  const value = cache.get(key);
  if (value === undefined) return undefined;
  // Reinsert to keep the most recently used entry at the end of the Map.
  cache.delete(key);
  cache.set(key, value);
  return value;
};

const setBoundedValue = (cache, key, value, maxEntries, onEvict) => {
  const previousValue = cache.get(key);
  cache.delete(key);
  if (previousValue !== undefined && previousValue !== value) {
    onEvict?.(previousValue);
  }
  cache.set(key, value);

  while (cache.size > maxEntries) {
    const [oldestKey, oldestValue] = cache.entries().next().value;
    cache.delete(oldestKey);
    onEvict?.(oldestValue);
  }
};

const destroyPdf = (pdf) => {
  if (!pdf?.destroy) return;
  Promise.resolve(pdf.destroy()).catch((error) => {
    console.warn("Failed to release cached PDF thumbnail document", error);
  });
};

const releasePdfDocument = (pendingPdf) => {
  pendingPdf
    ?.then(destroyPdf)
    // A rejected document was never created, so there is nothing to release.
    .catch(() => {});
};

const releaseThumbnailUrl = (thumbnailUrl) => {
  if (thumbnailUrl) URL.revokeObjectURL(thumbnailUrl);
};

const clearThumbnailCaches = () => {
  thumbnailCacheGeneration += 1;
  for (const pendingPdf of pdfDocPromiseCache.values()) {
    releasePdfDocument(pendingPdf);
  }
  for (const thumbnailUrl of pageThumbCache.values()) {
    releaseThumbnailUrl(thumbnailUrl);
  }
  pdfDocPromiseCache.clear();
  pageThumbCache.clear();
  pageCountCache.clear();
};

const isImageMime = (fileType) =>
  !!fileType && String(fileType).toLowerCase().startsWith("image/");

const isPdfMime = (fileType, fileUrl) => {
  const type = String(fileType || "").toLowerCase();
  if (type === "application/pdf" || type.includes("pdf")) return true;
  return /\.pdf($|\?)/i.test(String(fileUrl || ""));
};

const drawingIdOf = (drawing) => drawing?._id ?? drawing?.id ?? null;

const drawingLabelOf = (drawing, index) => {
  const number = String(drawing?.drawing_number || "").trim();
  if (number) return number;
  const title = String(drawing?.title || "").trim();
  if (title) return title.length > 18 ? `${title.slice(0, 16)}…` : title;
  return String(index + 1);
};

const pageLabelOf = (pageNumber, numPages) =>
  numPages <= 1 ? "1" : String(pageNumber);

const DrawingSectionHeader = ({ drawing, drawingIndex, isActive }) => {
  const number = String(drawing?.drawing_number || "").trim();
  const title = String(drawing?.title || "").trim();
  const fallback = drawingLabelOf(drawing, drawingIndex);

  return (
    <div
      style={{
        padding: "10px 8px 0",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <div
        title={[number, title].filter(Boolean).join(" — ") || fallback}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.25,
            color: isActive ? "#1677ff" : "#1a365d",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {number || fallback}
        </span>
        {title ? (
          <span
            style={{
              fontSize: 10,
              lineHeight: 1.3,
              color: isActive ? "#3b82f6" : "#8c8c8c",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {title}
          </span>
        ) : null}
      </div>
      <div
        style={{
          height: 1,
          width: "100%",
          background: isActive ? "#91caff" : "#d9d9d9",
        }}
      />
    </div>
  );
};

function getPdfDocPromise(fileUrl) {
  let pending = getCachedValue(pdfDocPromiseCache, fileUrl);
  if (!pending) {
    pending = pdfjsLib.getDocument(fileUrl).promise.catch((error) => {
      // Do not permanently cache a transient network or worker failure.
      if (pdfDocPromiseCache.get(fileUrl) === pending) {
        pdfDocPromiseCache.delete(fileUrl);
      }
      throw error;
    });
    setBoundedValue(
      pdfDocPromiseCache,
      fileUrl,
      pending,
      MAX_PDF_DOCUMENTS,
      releasePdfDocument,
    );
  }
  return pending;
}

async function getPdfPageCount(fileUrl) {
  const cachedCount = getCachedValue(pageCountCache, fileUrl);
  if (cachedCount !== undefined) return cachedCount;
  const pdf = await getPdfDocPromise(fileUrl);
  setBoundedValue(pageCountCache, fileUrl, pdf.numPages, MAX_PAGE_COUNTS);
  return pdf.numPages;
}

const canvasToObjectUrl = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          reject(new Error("Could not create thumbnail image blob"));
        }
      },
      "image/jpeg",
      0.85,
    );
  });

async function renderPdfPageObjectUrl(fileUrl, pageNumber) {
  const generation = thumbnailCacheGeneration;
  const key = pageCacheKey(fileUrl, pageNumber);
  const cached = getCachedValue(pageThumbCache, key);
  if (cached) return cached;

  const pdf = await getPdfDocPromise(fileUrl);
  const page = await pdf.getPage(pageNumber);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = THUMB_MAX_WIDTH / baseViewport.width;
  const viewport = page.getViewport({ scale: scale * 2 });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  const ctx = canvas.getContext("2d", { alpha: false });
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  const objectUrl = await canvasToObjectUrl(canvas);
  if (generation !== thumbnailCacheGeneration) {
    releaseThumbnailUrl(objectUrl);
    return null;
  }
  setBoundedValue(
    pageThumbCache,
    key,
    objectUrl,
    MAX_PAGE_THUMBNAILS,
    releaseThumbnailUrl,
  );
  return objectUrl;
}

const ThumbFrame = ({ isActive, children }) => (
  <div
    style={{
      width: "100%",
      boxSizing: "border-box",
      border: isActive ? "2px solid #1677ff" : "1px solid #d9d9d9",
      borderRadius: 2,
      overflow: "hidden",
      background: "#fff",
      boxShadow: isActive ? "0 0 0 1px rgba(22, 119, 255, 0.25)" : "none",
      minHeight: 64,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
    }}
  >
    {children}
  </div>
);

const ThumbButton = ({
  wrapRef,
  isActive,
  label,
  title,
  ariaLabel,
  onClick,
  children,
}) => (
  <button
    ref={wrapRef}
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    aria-current={isActive ? "page" : undefined}
    title={title}
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      width: "100%",
      padding: 8,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      borderRadius: 4,
    }}
  >
    <ThumbFrame isActive={isActive}>{children}</ThumbFrame>
    <span
      style={{
        fontSize: 11,
        lineHeight: 1.2,
        color: isActive ? "#1677ff" : "#595959",
        fontWeight: isActive ? 600 : 400,
        textAlign: "center",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  </button>
);

const ThumbPlaceholder = () => (
  <div
    style={{
      width: "100%",
      height: 72,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f5f5f5",
    }}
  >
    <FileOutlined style={{ fontSize: 18, color: "#bfbfbf" }} />
  </div>
);

/** Live pdf.js canvas thumb (active drawing — uses already-loaded pdfDoc). */
const LivePdfPageThumb = ({
  pdfDoc,
  pageNumber,
  isActive,
  label,
  title,
  onSelect,
}) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { rootMargin: "160px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || !pdfDoc || !canvasRef.current) return undefined;

    let cancelled = false;
    let renderTask = null;

    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNumber);
        if (cancelled || !canvasRef.current) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = THUMB_MAX_WIDTH / baseViewport.width;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d", { alpha: false });
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));
        renderTask = page.render({ canvasContext: ctx, viewport });
        await renderTask.promise;
      } catch (error) {
        if (error?.name !== "RenderingCancelledException") {
          console.warn("Page thumbnail render failed", error);
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        renderTask?.cancel();
      } catch {
        // ignore cancel errors
      }
    };
  }, [inView, pdfDoc, pageNumber]);

  return (
    <ThumbButton
      wrapRef={wrapRef}
      isActive={isActive}
      label={label}
      title={title}
      ariaLabel={`Go to page ${pageNumber}`}
      onClick={() => onSelect(pageNumber - 1)}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "block", width: "100%", height: "auto" }}
      />
    </ThumbButton>
  );
};

/** Cached data-URL thumb for other folder drawings (all pages). */
const CachedPdfPageThumb = ({
  fileUrl,
  pageNumber,
  isActive,
  label,
  title,
  onSelect,
}) => {
  const wrapRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [src, setSrc] = useState(
    () => getCachedValue(pageThumbCache, pageCacheKey(fileUrl, pageNumber)) || null,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { rootMargin: "160px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || !fileUrl) return undefined;
    let cancelled = false;

    (async () => {
      const cached = getCachedValue(pageThumbCache, pageCacheKey(fileUrl, pageNumber));
      if (cached) {
        setSrc(cached);
        setFailed(false);
        return;
      }
      try {
        const objectUrl = await renderPdfPageObjectUrl(fileUrl, pageNumber);
        if (!cancelled && objectUrl) {
          setSrc(objectUrl);
          setFailed(false);
        }
      } catch (error) {
        console.warn("Folder page thumbnail render failed", error);
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inView, fileUrl, pageNumber]);

  return (
    <ThumbButton
      wrapRef={wrapRef}
      isActive={isActive}
      label={label}
      title={title}
      ariaLabel={`Open ${label}`}
      onClick={() => onSelect(pageNumber - 1)}
    >
      {src && !failed ? (
        <img
          src={src}
          alt=""
          style={{ display: "block", width: "100%", height: "auto" }}
          onError={() => setFailed(true)}
        />
      ) : (
        <ThumbPlaceholder />
      )}
    </ThumbButton>
  );
};

const ImageSheetThumb = ({ drawing, label, title, isActive, onSelect }) => (
  <ThumbButton
    isActive={isActive}
    label={label}
    title={title}
    ariaLabel={`Open ${label}`}
    onClick={() => onSelect(0)}
  >
    <img
      src={drawing.file_url}
      alt=""
      style={{ display: "block", width: "100%", height: "auto" }}
    />
  </ThumbButton>
);

/**
 * Resolves page count for one drawing and renders a thumb for every page.
 */
const FolderDrawingPages = ({
  drawing,
  drawingIndex,
  isActiveDrawing,
  activePageIndex,
  activePdfDoc,
  activeNumPages,
  onSelect,
}) => {
  const fileUrl = drawing?.file_url;
  const fileType = drawing?.file_type;
  const groupRef = useRef(null);
  const [discoveredPages, setDiscoveredPages] = useState(() => {
    if (isActiveDrawing && activeNumPages > 0) return activeNumPages;
    if (fileUrl) {
      const cachedCount = getCachedValue(pageCountCache, fileUrl);
      if (cachedCount !== undefined) return cachedCount;
    }
    if (fileUrl && isImageMime(fileType)) return 1;
    return 0;
  });

  useEffect(() => {
    if (isActiveDrawing && activeNumPages > 0) {
      setDiscoveredPages(activeNumPages);
      if (fileUrl) {
        setBoundedValue(
          pageCountCache,
          fileUrl,
          activeNumPages,
          MAX_PAGE_COUNTS,
        );
      }
    }
  }, [isActiveDrawing, activeNumPages, fileUrl]);

  useEffect(() => {
    if (isActiveDrawing) return undefined;
    if (!fileUrl) return undefined;
    if (isImageMime(fileType)) {
      setDiscoveredPages(1);
      return undefined;
    }
    if (!isPdfMime(fileType, fileUrl)) {
      setDiscoveredPages(0);
      return undefined;
    }
    const cachedCount = getCachedValue(pageCountCache, fileUrl);
    if (cachedCount !== undefined) {
      setDiscoveredPages(cachedCount);
      return undefined;
    }

    let cancelled = false;
    const el = groupRef.current;
    let observer;

    const loadCount = async () => {
      try {
        const count = await getPdfPageCount(fileUrl);
        if (!cancelled) setDiscoveredPages(count);
      } catch (error) {
        console.warn("Failed to read PDF page count", error);
        if (!cancelled) setDiscoveredPages(0);
      }
    };

    if (!el || typeof IntersectionObserver === "undefined") {
      loadCount();
      return () => {
        cancelled = true;
      };
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          loadCount();
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(el);

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [isActiveDrawing, fileUrl, fileType]);

  const titleBase =
    [drawing?.drawing_number, drawing?.title].filter(Boolean).join(" — ") ||
    drawingLabelOf(drawing, drawingIndex);

  const header = (
    <DrawingSectionHeader
      drawing={drawing}
      drawingIndex={drawingIndex}
      isActive={isActiveDrawing}
    />
  );

  if (isImageMime(fileType) && fileUrl) {
    return (
      <div ref={groupRef}>
        {header}
        <ImageSheetThumb
          drawing={drawing}
          label={pageLabelOf(1, 1)}
          title={titleBase}
          isActive={isActiveDrawing && activePageIndex === 0}
          onSelect={onSelect}
        />
      </div>
    );
  }

  const numPages = discoveredPages;
  if (numPages < 1) {
    return (
      <div ref={groupRef}>
        {header}
        <ThumbButton
          isActive={false}
          label={pageLabelOf(1, 1)}
          title={titleBase}
          ariaLabel={drawingLabelOf(drawing, drawingIndex)}
          onClick={() => onSelect(0)}
        >
          <ThumbPlaceholder />
        </ThumbButton>
      </div>
    );
  }

  return (
    <div ref={groupRef}>
      {header}
      {Array.from({ length: numPages }, (_, index) => {
        const pageNumber = index + 1;
        const isActive = isActiveDrawing && activePageIndex === index;
        const label = pageLabelOf(pageNumber, numPages);
        const title =
          numPages > 1 ? `${titleBase} (page ${pageNumber})` : titleBase;

        if (isActiveDrawing && activePdfDoc) {
          return (
            <LivePdfPageThumb
              key={`${drawingIdOf(drawing)}-p${pageNumber}`}
              pdfDoc={activePdfDoc}
              pageNumber={pageNumber}
              isActive={isActive}
              label={label}
              title={title}
              onSelect={onSelect}
            />
          );
        }

        return (
          <CachedPdfPageThumb
            key={`${drawingIdOf(drawing)}-p${pageNumber}`}
            fileUrl={fileUrl}
            pageNumber={pageNumber}
            isActive={isActive}
            label={label}
            title={title}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
};

/**
 * Left rail: every drawing in the folder (list order), with every PDF page
 * stacked underneath. Default open; collapse with edge toggle.
 */
const PageThumbnailSidebar = ({
  drawings = [],
  activeDrawingId,
  activePageIndex = 0,
  activePdfDoc = null,
  activeNumPages = 0,
  onSelectDrawing,
  open = true,
  onOpenChange,
}) => {
  const activeRef = useRef(null);
  const width = open
    ? PAGE_THUMBNAIL_SIDEBAR_WIDTH
    : PAGE_THUMBNAIL_COLLAPSED_WIDTH;

  useEffect(() => {
    cacheConsumerCount += 1;
    return () => {
      cacheConsumerCount -= 1;
      if (cacheConsumerCount === 0) clearThumbnailCaches();
    };
  }, []);

  useEffect(() => {
    if (!open || !activeRef.current) return;
    activeRef.current.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [activeDrawingId, activePageIndex, open, drawings.length]);

  if (!Array.isArray(drawings) || drawings.length < 1) return null;

  return (
    <aside
      aria-label={open ? "Folder sheet thumbnails" : "Folder sheets collapsed"}
      style={{
        position: "relative",
        zIndex: 3,
        width,
        minWidth: width,
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid #f0f0f0",
        background: "#fafafa",
        boxSizing: "border-box",
        overflow: "visible",
        transition: SIDEBAR_TRANSITION,
      }}
    >
      <button
        type="button"
        onClick={() => onOpenChange?.(!open)}
        aria-label={open ? "Hide sheet thumbnails" : "Show sheet thumbnails"}
        title={open ? "Hide sheets" : "Show sheets"}
        style={toggleButtonStyle}
        onMouseEnter={(e) => applyToggleHover(e.currentTarget, true)}
        onMouseLeave={(e) => applyToggleHover(e.currentTarget, false)}
      >
        {open ? (
          <LeftOutlined style={{ fontSize: 9 }} />
        ) : (
          <RightOutlined style={{ fontSize: 9 }} />
        )}
      </button>
      <div
        aria-hidden={!open}
        style={{
          flex: 1,
          minHeight: 0,
          width: PAGE_THUMBNAIL_SIDEBAR_WIDTH,
          overflowY: open ? "auto" : "hidden",
          overflowX: "hidden",
          padding: "8px 0",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.2s ease",
        }}
      >
        {drawings.map((drawing, index) => {
          const id = drawingIdOf(drawing);
          const isActiveDrawing = id != null && id === activeDrawingId;
          return (
            <div
              key={id ?? `sheet-${index}`}
              ref={isActiveDrawing ? activeRef : undefined}
            >
              <FolderDrawingPages
                drawing={drawing}
                drawingIndex={index}
                isActiveDrawing={isActiveDrawing}
                activePageIndex={activePageIndex}
                activePdfDoc={activePdfDoc}
                activeNumPages={activeNumPages}
                onSelect={(pageIndex) => onSelectDrawing?.(drawing, pageIndex)}
              />
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default PageThumbnailSidebar;
